import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../../styling/stylingHelper';
import {
  sendTemplatedEmail,
  getAllCommunicationRecipientGroups,
  previewTemplatedEmail,
  downloadProductUpdatePdf,
  type CommunicationAdminGroup,
  type CommunicationStaffRecipient,
  type TemplatedEmailTemplate,
  type ProductUpdateContent,
} from '../../../../services/admin/adminService';
import { ProductUpdateComposer } from './ProductUpdateComposer';

/** The three templates, in the order they appear in the select. */
const TEMPLATES: TemplatedEmailTemplate[] = ['product_update', 'good_news', 'needs_input'];

const TEMPLATE_LABEL_KEYS: Record<TemplatedEmailTemplate, string> = {
  product_update: 'adminTesting.templatedEmail.templateProductUpdate',
  good_news: 'adminTesting.templatedEmail.templateGoodNews',
  needs_input: 'adminTesting.templatedEmail.templateNeedsInput',
};

const TEMPLATE_DESCRIPTION_KEYS: Record<TemplatedEmailTemplate, string> = {
  product_update: 'adminTesting.templatedEmail.templateProductUpdateDescription',
  good_news: 'adminTesting.templatedEmail.templateGoodNewsDescription',
  needs_input: 'adminTesting.templatedEmail.templateNeedsInputDescription',
};

/**
 * Send a templated communication email (product update / good news / input needed) styled like
 * our report emails. Test mode mails one typed-in address with a "[TEST] " subject prefix; real
 * mode shows every admin (pre-checked) with their approved staff nested underneath (unchecked)
 * for free per-person selection, behind a confirm step — one send to the composed list.
 */
export function TemplatedEmailCard() {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or effects
  // that depend on `t` re-run on every render (infinite fetch loop).
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);

  const [template, setTemplate] = useState<TemplatedEmailTemplate>('product_update');
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // Rich section content, built by the composer; null while nothing sendable exists.
  const [content, setContent] = useState<ProductUpdateContent | null>(null);

  const [isTest, setIsTest] = useState(true);
  const [testEmail, setTestEmail] = useState('');

  const [allRecipients, setAllRecipients] = useState<CommunicationAdminGroup[] | null>(null);
  const [allRecipientsLoading, setAllRecipientsLoading] = useState(false);
  const [allRecipientsError, setAllRecipientsError] = useState(false);
  /** True while a recipients fetch is in flight — see the load effect below. */
  const allRecipientsInFlight = useRef(false);
  const [selectedAllIds, setSelectedAllIds] = useState<string[]>([]);
  /** View-only filter over the real-mode recipient list — it never narrows what gets sent. */
  const [recipientSearch, setRecipientSearch] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState('');

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [sending, setSending] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Stable identity — the composer's change-effect depends on it.
  const handleContentChange = useCallback((next: ProductUpdateContent | null) => {
    setContent(next);
    setPreviewHtml(null);
  }, []);

  const isProductUpdate = template === 'product_update';

  // Real mode: load every admin (with staff nested) once. Admins start
  // pre-checked, staff unchecked — the super admin deselects demo accounts and
  // toggles staff on freely before confirming. The in-flight guard is a ref, NOT
  // the loading state: a loading-state dep makes the effect re-run the moment the
  // fetch starts, which fires the cleanup, marks the in-flight request cancelled
  // and leaves the card stuck on "Loading recipients..." forever. The error dep
  // stays — it stops a failed load (recipients still null) from retrying forever,
  // and the retry button clears it to load again.
  useEffect(() => {
    if (isTest || allRecipients !== null || allRecipientsError || allRecipientsInFlight.current) return;
    let cancelled = false;
    allRecipientsInFlight.current = true;
    setAllRecipientsLoading(true);
    const loadAll = async () => {
      try {
        const data = await getAllCommunicationRecipientGroups();
        if (cancelled) return;
        setAllRecipients(data);
        setSelectedAllIds(data.map(g => g.userId));
      } catch (err) {
        console.error('Failed to load all communication admins:', err);
        if (cancelled) return;
        setAllRecipientsError(true);
      } finally {
        allRecipientsInFlight.current = false;
        if (!cancelled) setAllRecipientsLoading(false);
      }
    };
    loadAll();
    // Clearing the ref lets StrictMode's second mount re-issue the fetch the
    // first one just abandoned.
    return () => { cancelled = true; allRecipientsInFlight.current = false; };
  }, [isTest, allRecipients, allRecipientsError]);

  const templateLabel = t(TEMPLATE_LABEL_KEYS[template]);
  /** What the server falls back to when the subject field is left empty. */
  const defaultSubject = `${templateLabel} — ${title.trim()}`;

  const isTestEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail);
  // Flat person list for the confirm dialog (staff under two admins appears
  // once — the server dedups the same way).
  const selectedAllRecipients = useMemo(() => {
    const seen = new Set<string>();
    const people: { userId: string; email: string }[] = [];
    for (const group of allRecipients ?? []) {
      for (const person of [group, ...group.staff]) {
        if (selectedAllIds.includes(person.userId) && !seen.has(person.userId)) {
          seen.add(person.userId);
          people.push({ userId: person.userId, email: person.email });
        }
      }
    }
    return people;
  }, [allRecipients, selectedAllIds]);

  const extraEmails = Array.from(new Set(
    additionalEmails.split(/[,;\s]+/).map(email => email.trim().toLowerCase()).filter(Boolean)
  ));
  const invalidExtraEmails = extraEmails.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  const realRecipientEmails = Array.from(new Set([
    ...selectedAllRecipients.map(person => person.email.trim().toLowerCase()),
    ...extraEmails.filter(email => !invalidExtraEmails.includes(email)),
  ]));

  // Search is a VIEW filter: it narrows what the list shows, never `selectedAllIds`, so a
  // selection made under one query survives the next one. A group stays visible when the admin
  // (name, email or one of their restaurants) matches — all its staff then show — or when only
  // some of its staff match, in which case just those staff show under their admin.
  const recipientQuery = recipientSearch.trim().toLowerCase();
  const filteredRecipients = useMemo(() => {
    const groups = allRecipients ?? [];
    if (recipientQuery === '') return groups.map(group => ({ group, staff: group.staff }));
    const hit = (...fields: (string | null | undefined)[]) =>
      fields.some(field => (field ?? '').toLowerCase().includes(recipientQuery));
    const visible: { group: CommunicationAdminGroup; staff: CommunicationStaffRecipient[] }[] = [];
    for (const group of groups) {
      if (hit(group.name, group.email, ...group.restaurantNames)) {
        visible.push({ group, staff: group.staff });
        continue;
      }
      const staff = group.staff.filter(person => hit(person.name, person.email, person.restaurantRole));
      if (staff.length > 0) visible.push({ group, staff });
    }
    return visible;
  }, [allRecipients, recipientQuery]);

  // Everyone the list currently shows — the scope of the two bulk buttons. Deduped: the same
  // staff account can sit under two admins, and counting it twice would inflate the labels.
  const visibleUserIds = useMemo(
    () => Array.from(new Set(
      filteredRecipients.flatMap(({ group, staff }) => [group.userId, ...staff.map(p => p.userId)])
    )),
    [filteredRecipients]
  );
  const visibleSelectedCount = visibleUserIds.filter(id => selectedAllIds.includes(id)).length;
  /** Selected people the current search hides — they still go out, so the list says so. */
  const hiddenSelectedCount = selectedAllRecipients.length - visibleSelectedCount;

  /** Bulk toggle over the shown accounts only; selections outside the search are left alone. */
  const setVisibleRecipients = (selected: boolean) => {
    setSelectedAllIds(prev =>
      selected
        ? Array.from(new Set([...prev, ...visibleUserIds]))
        : prev.filter(id => !visibleUserIds.includes(id))
    );
  };

  const contentReady = isProductUpdate ? content !== null : body.trim() !== '';
  const canSend =
    title.trim() !== '' &&
    contentReady &&
    !sending &&
    (isTest ? isTestEmailValid :
      !allRecipientsLoading && realRecipientEmails.length > 0 && invalidExtraEmails.length === 0);

  const toggleAllRecipient = (userId: string) => {
    setSelectedAllIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleTestMode = (nextIsTest: boolean) => {
    setIsTest(nextIsTest);
    setRecipientSearch('');
    setMessage(null);
  };

  const handlePreview = async () => {
    if (title.trim() === '' || !contentReady) return;
    try {
      setPreviewLoading(true);
      setMessage(null);
      const result = await previewTemplatedEmail({
        template,
        title: title.trim(),
        subject: subject.trim() || undefined,
        body: isProductUpdate ? undefined : body,
        content: isProductUpdate ? content ?? undefined : undefined,
      });
      setPreviewHtml(result.html);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || t('adminTesting.templatedEmail.previewError') });
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!isProductUpdate || !title.trim() || !content || downloadingPdf) return;
    setDownloadingPdf(true);
    setMessage(null);
    try {
      const blob = await downloadProductUpdatePdf({ title: title.trim(), content });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'product-update.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Give the browser time to begin reading the blob before releasing it.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to download product update PDF:', err);
      setMessage({ type: 'error', text: t('adminTesting.templatedEmail.pdfError') });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSend = async () => {
    if (!canSend) return;

    // Real sends cannot be taken back — always confirm the combined recipient list first.
    if (!isTest) {
      const emails = realRecipientEmails;
      // An all-customers list can be long — keep the confirm dialog readable.
      const emailPreview =
        emails.length > 30 ? `${emails.slice(0, 30).join('\n')}\n… +${emails.length - 30} more` : emails.join('\n');
      const confirmed = window.confirm(
        t('adminTesting.templatedEmail.confirmReal', {
          count: emails.length,
          subject: subject.trim() || defaultSubject,
          recipients: emailPreview,
        })
      );
      if (!confirmed) return;
    }

    try {
      setSending(true);
      setMessage(null);
      const result = await sendTemplatedEmail({
        template,
        title: title.trim(),
        subject: subject.trim() || undefined,
        body: isProductUpdate ? undefined : body,
        content: isProductUpdate ? content ?? undefined : undefined,
        isTest,
        testEmail: isTest ? testEmail.trim() : undefined,
        scope: isTest ? undefined : 'all',
        recipientUserIds: isTest ? undefined : selectedAllIds,
        additionalEmails: isTest ? undefined : extraEmails,
      });
      const failureList = result.failures.map(f => `${f.email}: ${f.error}`).join(', ');
      if (result.failed === 0) {
        setMessage({
          type: 'success',
          text: t('adminTesting.templatedEmail.sendSuccess', { count: result.sent }),
        });
      } else if (result.sent > 0) {
        setMessage({
          type: 'error',
          text: t('adminTesting.templatedEmail.sendPartial', {
            sent: result.sent,
            failed: result.failed,
            failures: failureList,
          }),
        });
      } else {
        setMessage({
          type: 'error',
          text: t('adminTesting.templatedEmail.sendAllFailed', {
            count: result.failed,
            failures: failureList,
          }),
        });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || t('adminTesting.templatedEmail.sendError') });
    } finally {
      setSending(false);
    }
  };

  const inputClass = cn(
    'w-full px-3 py-2 rounded-lg border',
    'border-gray-300 dark:border-gray-600',
    'bg-white dark:bg-gray-700',
    primaryText, smallTextSize
  );

  return (
    /* Turns red in real mode — this card mails live customers */
    <div className={cn(
      cardClass,
      'p-6',
      !isTest && 'border-2 border-red-500 dark:border-red-500 bg-red-50/50 dark:bg-red-900/10'
    )}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.templatedEmail.title')}</h3>
          <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
            {t('adminTesting.templatedEmail.description')}
          </p>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer">
          <input
            type="checkbox"
            checked={!isTest}
            onChange={(e) => handleToggleTestMode(!e.target.checked)}
            className="w-4 h-4 accent-red-600"
          />
          <span className={cn(smallTextSize, semiBoldText, !isTest ? 'text-red-600 dark:text-red-400' : secondaryText)}>
            {t('adminTesting.templatedEmail.realModeToggle')}
          </span>
        </label>
      </div>

      {!isTest && (
        <div className="mb-4 p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">
          {t('adminTesting.templatedEmail.realModeWarning')}
        </div>
      )}

      {/* Template */}
      <div className="mb-3">
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.templateLabel')}
        </label>
        <select
          value={template}
          onChange={(e) => setTemplate(e.target.value as TemplatedEmailTemplate)}
          className={inputClass}
        >
          {TEMPLATES.map(value => (
            <option key={value} value={value}>{t(TEMPLATE_LABEL_KEYS[value])}</option>
          ))}
        </select>
        <p className={cn(secondaryText, 'text-xs mt-1')}>{t(TEMPLATE_DESCRIPTION_KEYS[template])}</p>
      </div>

      {/* Title */}
      <div className="mb-3">
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.titleLabel')}
        </label>
        <input
          type="text"
          placeholder={t('adminTesting.templatedEmail.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={inputClass}
        />
      </div>

      {/* Subject — empty means the server uses "<template label> — <title>" */}
      <div className="mb-3">
        <label className={cn(secondaryText, 'text-xs block mb-1')}>
          {t('adminTesting.templatedEmail.subjectLabel')}
        </label>
        <input
          type="text"
          placeholder={title.trim() ? defaultSubject : t('adminTesting.templatedEmail.subjectPlaceholderEmpty')}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={inputClass}
        />
        <p className={cn(secondaryText, 'text-xs mt-1')}>
          {t('adminTesting.templatedEmail.subjectHint')}
        </p>
      </div>

      {/* Body: rich section composer for product updates, plain text for the rest */}
      {isProductUpdate ? (
        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.templatedEmail.composer.label')}
          </label>
          <ProductUpdateComposer t={t} onChange={handleContentChange} onApplyTitle={setTitle} />
        </div>
      ) : (
        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.templatedEmail.bodyLabel')}
          </label>
          <textarea
            placeholder={t('adminTesting.templatedEmail.bodyPlaceholder')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className={inputClass}
          />
          <p className={cn(secondaryText, 'text-xs mt-1')}>
            {t('adminTesting.templatedEmail.bodyHint')}
          </p>
        </div>
      )}

      {/* Preview — the exact server-rendered HTML the recipient gets */}
      <div className="mb-3">
        <button
          type="button"
          onClick={previewHtml ? () => setPreviewHtml(null) : handlePreview}
          disabled={previewLoading || title.trim() === '' || !contentReady}
          className={cn(
            'px-3 py-1.5 rounded-lg border transition-colors',
            'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            secondaryText, smallTextSize
          )}
        >
          {previewLoading
            ? t('adminTesting.templatedEmail.previewLoading')
            : previewHtml
              ? t('adminTesting.templatedEmail.previewHide')
              : t('adminTesting.templatedEmail.previewShow')}
        </button>
        {previewHtml && (
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            sandbox=""
            className="w-full h-[560px] mt-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-white"
          />
        )}
      </div>

      {isTest ? (
        /* Test send: one typed-in address, subject prefixed [TEST] */
        <div className="mb-3">
          <label className={cn(secondaryText, 'text-xs block mb-1')}>
            {t('adminTesting.templatedEmail.testEmailLabel')}
          </label>
          <input
            type="email"
            placeholder={t('adminTesting.templatedEmail.testEmailPlaceholder')}
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className={inputClass}
          />
        </div>
      ) : (
        /* Real send: every admin with staff nested — free per-person selection */
          <div className="mb-3">
              <label className={cn(secondaryText, 'text-xs block mb-1')}>
                {t('adminTesting.templatedEmail.allRecipientsLabel', { count: selectedAllRecipients.length })}
              </label>
              <p className={cn(secondaryText, 'text-xs mb-2')}>
                {t('adminTesting.templatedEmail.allRecipientsHint')}
              </p>
              {allRecipientsLoading ? (
                <p className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.templatedEmail.recipientsLoading')}
                </p>
              ) : allRecipientsError ? (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {t('adminTesting.templatedEmail.recipientsError')}{' '}
                  <button
                    type="button"
                    onClick={() => setAllRecipientsError(false)}
                    className="underline"
                  >
                    {t('adminTesting.templatedEmail.recipientsRetry')}
                  </button>
                </p>
              ) : !allRecipients || allRecipients.length === 0 ? (
                <p className={cn(secondaryText, 'text-xs')}>
                  {t('adminTesting.templatedEmail.recipientsNone')}
                </p>
              ) : (
                <>
                  {/* Search + bulk toggles: wraps so the buttons drop below the field on a narrow screen */}
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <div className="relative flex-1 min-w-[12rem]">
                      <input
                        type="text"
                        value={recipientSearch}
                        onChange={(e) => setRecipientSearch(e.target.value)}
                        placeholder={t('adminTesting.templatedEmail.recipientsSearchPlaceholder')}
                        className={cn(inputClass, recipientSearch !== '' && 'pr-14')}
                      />
                      {recipientSearch !== '' && (
                        <button
                          type="button"
                          onClick={() => setRecipientSearch('')}
                          className={cn('absolute right-2 top-1/2 -translate-y-1/2 text-xs underline', secondaryText)}
                        >
                          {t('adminTesting.templatedEmail.recipientsSearchClear')}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        onClick={() => setVisibleRecipients(true)}
                        disabled={visibleSelectedCount === visibleUserIds.length}
                        className={cn(
                          'px-2 py-1 rounded-full border text-xs transition-colors',
                          'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                          'hover:bg-gray-50 dark:hover:bg-gray-600',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          secondaryText
                        )}
                      >
                        {t('adminTesting.templatedEmail.recipientsSelectAll', { count: visibleUserIds.length })}
                      </button>
                      <button
                        type="button"
                        onClick={() => setVisibleRecipients(false)}
                        disabled={visibleSelectedCount === 0}
                        className={cn(
                          'px-2 py-1 rounded-full border text-xs transition-colors',
                          'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600',
                          'hover:bg-gray-50 dark:hover:bg-gray-600',
                          'disabled:opacity-50 disabled:cursor-not-allowed',
                          secondaryText
                        )}
                      >
                        {t('adminTesting.templatedEmail.recipientsDeselectAll', { count: visibleSelectedCount })}
                      </button>
                    </div>
                  </div>
                  {hiddenSelectedCount > 0 && (
                    <p className="text-xs mb-2 text-amber-700 dark:text-amber-300">
                      {t('adminTesting.templatedEmail.recipientsHiddenSelectedHint', { count: hiddenSelectedCount })}
                    </p>
                  )}
                  {filteredRecipients.length === 0 ? (
                    <p className={cn(secondaryText, 'text-xs')}>
                      {t('adminTesting.templatedEmail.recipientsSearchNone', { query: recipientSearch.trim() })}
                    </p>
                  ) : (
                    <div className="border border-red-200 dark:border-red-800 rounded-lg divide-y divide-red-100 dark:divide-red-900/50 max-h-72 overflow-y-auto">
                    {filteredRecipients.map(({ group, staff }) => (
                      <div key={group.userId}>
                        <label className="flex items-center gap-2 px-3 py-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedAllIds.includes(group.userId)}
                            onChange={() => toggleAllRecipient(group.userId)}
                            className="w-4 h-4 accent-red-600"
                          />
                          <span className="flex-1 min-w-0">
                            <span className={cn(primaryText, smallTextSize, semiBoldText, 'block truncate')}>{group.name || group.email}</span>
                            <span className={cn(secondaryText, 'text-xs block truncate')}>{group.email}</span>
                          </span>
                          <span className={cn(secondaryText, 'text-xs shrink-0 max-w-[40%] truncate')}>
                            {group.restaurantNames.join(', ')}
                          </span>
                        </label>
                        {staff.map(person => (
                          <label key={person.userId} className="flex items-center gap-2 pl-9 pr-3 py-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedAllIds.includes(person.userId)}
                              onChange={() => toggleAllRecipient(person.userId)}
                              className="w-4 h-4 accent-red-600"
                            />
                            <span className="flex-1 min-w-0">
                              <span className={cn(primaryText, smallTextSize, 'block truncate')}>{person.name || person.email}</span>
                              <span className={cn(secondaryText, 'text-xs block truncate')}>{person.email}</span>
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded shrink-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              {person.restaurantRole || t('adminTesting.templatedEmail.recipientsNoRole')}
                            </span>
                          </label>
                        ))}
                      </div>
                    ))}
                    </div>
                  )}
                </>
              )}
              <div className="mt-3">
                <label htmlFor="templated-additional-emails" className={cn(secondaryText, 'text-xs block mb-1')}>
                  {t('adminTesting.templatedEmail.additionalEmailsLabel')}
                </label>
                <textarea
                  id="templated-additional-emails"
                  value={additionalEmails}
                  onChange={e => setAdditionalEmails(e.target.value)}
                  rows={3}
                  disabled={sending}
                  aria-invalid={invalidExtraEmails.length > 0}
                  aria-describedby="templated-additional-emails-hint templated-additional-emails-error"
                  placeholder={t('adminTesting.templatedEmail.additionalEmailsPlaceholder')}
                  className={inputClass}
                />
                <p id="templated-additional-emails-hint" className={cn(secondaryText, 'text-xs mt-1')}>
                  {t('adminTesting.templatedEmail.additionalEmailsHint')}
                </p>
                <p id="templated-additional-emails-error" role="alert" className="text-xs mt-1 break-words text-red-600 dark:text-red-400">
                  {invalidExtraEmails.length > 0 && t('adminTesting.templatedEmail.additionalEmailsInvalid', {
                    emails: invalidExtraEmails.join(', '),
                  })}
                </p>
              </div>
          </div>
      )}

      {message && (
        <div className={cn(
          'mb-3 p-3 rounded-lg text-sm',
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        )}>
          {message.text}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={cn(
            'px-4 py-2 rounded-lg transition-colors text-white dark:text-white',
            isTest
              ? 'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700'
              : 'bg-red-600 dark:bg-red-600 hover:bg-red-700 dark:hover:bg-red-700',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            smallTextSize, semiBoldText
          )}
        >
          {sending
            ? t('adminTesting.templatedEmail.sending')
            : isTest
              ? t('adminTesting.templatedEmail.sendTestButton')
              : t('adminTesting.templatedEmail.sendRealButton', { count: realRecipientEmails.length })}
        </button>
        {isProductUpdate && (
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || !title.trim() || !contentReady}
            className={cn(
              'px-4 py-2 rounded-lg border transition-colors',
              'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              secondaryText, smallTextSize, semiBoldText
            )}
          >
            {t(`adminTesting.templatedEmail.${downloadingPdf ? 'pdfDownloading' : 'downloadPdf'}`)}
          </button>
        )}
      </div>
    </div>
  );
}
